import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button, Card, CardHeader, Input, PageContainer } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  sendPhishingVerificationCode,
  verifyPhishingCode,
  startPhishingTest,
} from '../api/api';

export default function PhishingManual() {
  const { organizationId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [code, setCode] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const canBypass = user?.canBypassPhishingVerification === true || user?.role === 'ADMIN';

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!authorized) {
      setError(t.mustConfirmEmail);
      return;
    }
    if (!email?.trim()) {
      setError(t.yourEmail || 'Informe seu e-mail');
      return;
    }
    setLoading(true);
    try {
      await sendPhishingVerificationCode(email.trim(), authorized);
      setCodeSent(true);
      setCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!code?.trim() || code.trim().length !== 6) {
      setError(t.enterVerificationCode || 'Digite o código de 6 dígitos');
      return;
    }
    setLoadingVerify(true);
    try {
      const res = await verifyPhishingCode(email.trim(), code.trim());
      setVerificationToken(res.verificationToken);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleSendSimulation = async (e, skipVerification = false) => {
    e.preventDefault();
    setError('');
    if (!authorized) {
      setError(t.mustConfirmEmail);
      return;
    }
    if (!skipVerification && !verificationToken) {
      setError(t.mustConfirmEmail || 'É necessário verificar o e-mail antes de enviar.');
      return;
    }
    setLoading(true);
    try {
      await startPhishingTest(
        email.trim(),
        authorized,
        skipVerification ? null : verificationToken,
        skipVerification
      );
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <PageContainer>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100 tracking-tight">
            {t.manualPhishingSim}
          </h1>
          <p className="text-[#475569] dark:text-slate-400 mt-1">{t.manualPhishingDesc}</p>
        </div>

        <div className="max-w-3xl">
          <div className="bg-amber-100 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-400 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">⚠️ {t.phishingAttentionManual}</p>
          </div>
        </div>

        {success ? (
          <Card className="max-w-3xl overflow-hidden">
            <CardHeader title={t.emailSentSuccess} />
            <div className="p-6">
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {t.emailSentDesc?.replace('{email}', email) ||
                  `Um e-mail de simulação foi enviado para ${email}. Verifique sua caixa de entrada.`}
              </p>
              <Link
                to={`/org/${organizationId}/simulations`}
                className="text-cyan-500 hover:text-cyan-600 dark:text-cyan-400 dark:hover:text-cyan-300 font-medium"
              >
                ← {t.backToSimulations}
              </Link>
            </div>
          </Card>
        ) : (
          <Card className="max-w-3xl overflow-hidden">
            <CardHeader title={t.sendSimulation} subtitle={t.manualPhishingDesc} />
            <div className="p-6">
              <form className="space-y-5">
            {error && (
              <div className="bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-500/30 px-4 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* 1. Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t.yourEmail}
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setCodeSent(false);
                  setVerificationToken(null);
                }}
                placeholder={t.emailPlaceholder}
                required
              />
            </div>

            {/* 2. Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="authorized"
                checked={authorized}
                onChange={(e) => setAuthorized(e.target.checked)}
                className="mt-1 h-4 w-4 rounded accent-cyan-500"
              />
              <label htmlFor="authorized" className="text-sm text-slate-600 dark:text-slate-300">
                {t.confirmEmailAuth}
              </label>
            </div>

            {/* 3. Send verification code */}
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={handleSendCode}
                disabled={loading || !email?.trim() || !authorized}
                className="w-full"
              >
                {loading && !codeSent ? '...' : t.sendVerificationCode}
              </Button>
              {codeSent && (
                <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                  {t.verificationCodeSent}
                </p>
              )}
            </div>

            {/* 4 & 5. Code input + Verify */}
            {codeSent && !verificationToken && (
              <div className="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t.enterVerificationCode}
                  </label>
                  <Input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-lg tracking-[0.3em]"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={loadingVerify || code.length !== 6}
                  className="w-full"
                >
                  {loadingVerify ? '...' : t.verifyCode}
                </Button>
              </div>
            )}

            {/* 6. Send simulation - after verification (or bypass for authorized users) */}
            {(verificationToken || canBypass) && (
              <div className="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                {verificationToken && (
                  <p className="text-sm text-green-600 dark:text-green-400">{t.codeVerified}</p>
                )}
                {verificationToken && (
                  <Button
                    type="button"
                    onClick={(e) => handleSendSimulation(e, false)}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? t.sending : t.sendEmail}
                  </Button>
                )}
                {canBypass && (
                  <Button
                    type="button"
                    onClick={(e) => handleSendSimulation(e, true)}
                    disabled={loading || !email?.trim() || !authorized}
                    variant="secondary"
                    className="w-full text-sm"
                  >
                    {t.sendDirectly || t.bypassVerification}
                  </Button>
                )}
              </div>
            )}
              </form>
            </div>
          </Card>
        )}
      </PageContainer>
    </Layout>
  );
}
